import { createStore } from 'solid-js/store';
import { ApiError, api, errMsg } from '~shared/lib/api';
import { airtableUrl } from '~shared/lib/airtable';
import { buildHistoryIndex, type HistoryIndex } from '~/lib/history';
import type { MemberSnapshot } from '~/lib/types';

type State = {
  snapshot: MemberSnapshot | null;
  index: HistoryIndex | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
};

const [state, setState] = createStore<State>({
  snapshot: null,
  index: null,
  loading: false,
  refreshing: false,
  error: null,
});

export const snapshotState = state;

export const memberUrl = (id: string): string =>
  airtableUrl(state.snapshot?.tableIds.members ?? '', id);
export const teamUrl = (id: string): string =>
  airtableUrl(state.snapshot?.tableIds.teams ?? '', id);
export const clinicUrl = (id: string): string =>
  airtableUrl(state.snapshot?.tableIds.clinics ?? '', id);

function apply(snapshot: MemberSnapshot): void {
  setState({ snapshot, index: buildHistoryIndex(snapshot) });
}

const STALE_AFTER_MS = 12 * 60 * 60 * 1000;

function ageMs(snapshot: MemberSnapshot): number {
  const age = Date.now() - new Date(snapshot.generatedAt).getTime();
  return Number.isFinite(age) ? age : Infinity;
}

export async function initSnapshot(): Promise<void> {
  setState({ loading: true, error: null });

  let cached: MemberSnapshot | null = null;
  try {
    cached = await api<MemberSnapshot>('/snapshot');
    apply(cached);
  } catch (err) {
    if (!(err instanceof ApiError && err.status === 404)) {
      setState({ loading: false, error: errMsg(err) });
      return;
    }
  }
  setState({ loading: false });

  if (!cached || ageMs(cached) > STALE_AFTER_MS) await refreshSnapshot();
}

export async function refreshSnapshot(): Promise<void> {
  setState({ refreshing: true, error: null });
  try {
    apply(await api<MemberSnapshot>('/airtable/refresh', { method: 'POST' }));
    setState({ refreshing: false });
  } catch (err) {
    setState({ refreshing: false, error: errMsg(err) });
  }
}
